// Cashback campaigns API (maintenance). Base = /api/maintenance/cashback.
// Types mirror admin-api app/schemas/maintenance/campaigns.py exactly.
import axiosInstance from "../axiosInstance";

const BASE = "/api/maintenance/cashback";

export type CampaignKind = "offer" | "announcement";
export type CampaignStatus = "draft" | "scheduled" | "live" | "ended" | "cancelled";
export type Audience = "all" | "near_miss" | "benefited" | "dormant" | "holding_promo" | "list";
export type Visibility = "public" | "private";
export type PerUserReset = "campaign" | "day" | "week" | "month";
export type OnExhausted = "fall_back" | "stop_hard" | "continue_and_alert";

export interface CampaignTier {
  id?: number;
  campaign_id?: number;
  name: string;
  min_amount: number;
  max_amount: number | null;
  is_random: boolean;
  min_percentage: number | null;
  max_percentage: number | null;
  fixed_percentage: number | null;
  priority: number;
  is_active: boolean;
}

/** When a campaign is actually paying, inside its outer dates. Generated from
 *  `recurrence` at schedule time and frozen. No windows means one window — the
 *  whole outer range — which is what every pre-v2 campaign has. */
export interface RunWindow {
  starts_at: string;
  ends_at: string;
}

export type Recurrence =
  | { type: "daily"; from: string; to: string }
  | { type: "weekly"; days: number[]; from: string; to: string }
  | { type: "monthly_first_n"; n: number };

export interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}

/** The surfaces a campaign's promo may use. There is deliberately no
 *  full-screen option: both app stores restrict unexpected interstitials. */
export const BOOST_SURFACES = ["badge", "home", "inline", "topup", "post_session"] as const;
export type BoostSurface = (typeof BOOST_SURFACES)[number];

/** How hard one campaign may push itself, and where — the tunable half.
 *  Mirrors CampaignBoost in admin-api app/schemas/maintenance/campaigns.py.
 *  The fixed floors (4 interruptions per user per rolling 7 days across all
 *  campaigns, 1 per session, the dismissal decay, the global opt-out) are in
 *  code on both sides and are not reachable from here. */
export interface CampaignBoost {
  surfaces: BoostSurface[];
  per_7d: number;
  cooldown_hours: number;
  lifetime: number;
  social_proof: boolean;
  social_proof_min: number;
  /** which campaign is OFFERED when several are eligible — not Campaign.priority,
   *  which decides which one PAYS */
  priority: number;
  /** the kill switch for this campaign's promo */
  enabled: boolean;
  /** presentation only; null is no theme */
  theme?: "diwali" | null;
}

/** A null `boost` column means exactly this. Same values as the server's
 *  field defaults, so "default" has one definition rather than two. */
export const BOOST_DEFAULTS: CampaignBoost = {
  surfaces: ["inline", "topup", "post_session"],
  per_7d: 1,
  cooldown_hours: 72,
  lifetime: 3,
  social_proof: false,
  social_proof_min: 50,
  priority: 100,
  enabled: true,
  theme: null,
};

/** [min, max]; null max means no ceiling. Mirrors BOOST_BOUNDS server-side,
 *  which REFUSES an out-of-range value with a 400 rather than clamping it —
 *  so these only keep the input honest, they are not the authority. */
export const BOOST_BOUNDS: Record<
  "per_7d" | "cooldown_hours" | "lifetime" | "social_proof_min" | "priority",
  [number, number | null]
> = {
  per_7d: [1, 3],
  cooldown_hours: [48, 336],
  lifetime: [1, 4],
  social_proof_min: [0, null],
  priority: [0, null],
};

export interface Campaign {
  id: number;
  campaign_guid: string;
  program_id: number;
  name: string;
  slug: string;
  kind: CampaignKind;
  status: CampaignStatus;
  starts_at: string;
  ends_at: string;
  org_ids: number[] | null;
  audience: Audience;
  audience_snapshot: Record<string, any> | null;
  min_payment_amount: number | null;
  max_cashback_cap: number | null;
  per_user_cap: number | null;
  per_user_award_limit: number | null;
  per_user_reset: PerUserReset;
  budget_cap: number | null;
  max_awards: number | null;
  on_budget_exhausted: OnExhausted;
  awarded_count: number;
  awarded_amount: number;
  visibility: Visibility;
  priority: number | null;
  headline: string | null;
  subline: string | null;
  terms: string | null;
  announce_push: boolean;
  announce_inapp: boolean;
  announce_at: string | null;
  announced_at: string | null;
  reminder_at: string | null;
  reminded_at: string | null;
  teaser_at: string | null;
  teased_at: string | null;
  image_url: string | null;
  tags: string[];
  utm: Utm | null;
  recurrence: Recurrence | null;
  /** null means BOOST_DEFAULTS — see migration 035 */
  boost: CampaignBoost | null;
  /** Detail response only — the windows the recurrence expanded into. */
  windows?: RunWindow[];
  /** Live AND inside a run window, i.e. paying right now. A live campaign
   *  between windows is `active_now: false` — status does not flicker. */
  active_now?: boolean;
  next_window?: RunWindow | null;
  lead_hours_required: number | null;
  lead_waiver_id: number | null;
  forecast: Forecast | null;
  created_by: string;
  created_at: string;
  scheduled_by: string | null;
  scheduled_at: string | null;
  ended_by: string | null;
  ended_at: string | null;
  updated_at: string;
  tier_count?: number | null;
  member_count?: number | null;
}

export interface CampaignCreate {
  name: string;
  slug?: string | null;
  kind?: CampaignKind;
  starts_at: string;
  ends_at: string;
  org_ids?: number[] | null;
  audience?: Audience;
  min_payment_amount?: number | null;
  max_cashback_cap?: number | null;
  per_user_cap?: number | null;
  per_user_award_limit?: number | null;
  per_user_reset?: PerUserReset;
  budget_cap?: number | null;
  max_awards?: number | null;
  on_budget_exhausted?: OnExhausted;
  visibility?: Visibility;
  priority?: number | null;
  headline?: string | null;
  subline?: string | null;
  terms?: string | null;
  announce_push?: boolean;
  announce_inapp?: boolean;
  announce_at?: string | null;
  reminder_at?: string | null;
  teaser_at?: string | null;
  image_url?: string | null;
  tags?: string[];
  utm?: Utm | null;
  recurrence?: Recurrence | null;
  tiers?: CampaignTier[];
}

export interface CampaignUpdate extends Partial<Omit<CampaignCreate, "tiers">> {
  // optional limits set to null explicitly
  clear?: string[];
}

export interface SlotValueReading {
  pct: number;
  per_100_becomes: number;
  effective_discount_pct: number;
  free_kwh_on_basis: number;
  free_sessions_on_basis: number;
}
export interface SlotValue {
  tier_id: number;
  name: string;
  lo: SlotValueReading;
  hi: SlotValueReading;
  basis: number;
}

export interface ForecastSlot {
  slot: string;
  topups: number;
  users: number;
  gross: number;
  payout: number;
  share_of_gross: number;
}
export interface ForecastBinds {
  limit: "max_awards" | "budget_cap";
  on_day: number | null;
  spent_when_bound: number;
}
export interface Forecast {
  history_days: number;
  window_days: number;
  history: {
    topups_total: number; users_total: number; gross_total: number;
    qualifying_topups: number; qualifying_users: number; qualifying_gross: number;
    payout: number; share_of_gross: number; users_hit_allowance: number;
  };
  window: {
    topups: number; payout: number; budget_cap: number | null; max_awards: number | null;
    budget_share_used: number | null; binds_first: ForecastBinds | null;
  };
  per_slot: ForecastSlot[];
  vs_standing?: {
    standing_payout_window: number; campaign_payout_window: number; delta_window: number;
    standing_qualifying: number; campaign_qualifying: number;
  };
  generated_at?: string;
}

export interface ForecastResponse { forecast: Forecast; slot_values: SlotValue[]; }

export interface AuditRow {
  id: number; at: string; actor: string; actor_type: "operator" | "system"; action: string;
  from_status: string | null; to_status: string | null; detail: Record<string, any> | null;
}

export interface LaunchRequest {
  id: number; request_guid: string; campaign_id: number; requested_starts_at: string;
  lead_hours_actual: number; lead_hours_required: number; reason: string;
  status: "pending" | "approved" | "rejected" | "expired";
  requested_by: string; requested_at: string; decided_by: string | null; decided_at: string | null;
  decision_note: string | null; self_approved: boolean;
  campaign_name: string; campaign_slug: string; campaign_status: CampaignStatus;
}

export interface Performance {
  campaign_id: number; status: CampaignStatus; forecast: Forecast | null;
  counters: {
    awarded_count: number; awarded_amount: number;
    budget_cap: number | null; max_awards: number | null;
    max_cashback_cap: number | null;
    per_user_award_limit: number | null; per_user_cap: number | null;
    per_user_reset: PerUserReset;
    on_budget_exhausted: OnExhausted;
  };
  by_state: Record<string, { count: number; amount: number }>;
  per_slot: { slot: string | null; n: number; payout: number; gross: number }[];
  per_day: { day: string; n: number; payout: number }[];
  users: { users: number; first_time_users: number };
  clawed_back: number;
  limit_events: Record<string, number>;
}

export interface CampaignDetail {
  campaign: Campaign;
  tiers: CampaignTier[];
  member_count: number;
  slot_values: SlotValue[];
  audit: AuditRow[];
  performance: Performance | null;
  pending_launch_request: LaunchRequest | null;
}

export interface Member {
  user_id: number; note: string | null; rate_override_pct: number | null; added_by: string; added_at: string;
  user_name: string | null; phone: string | null; organization: string | null;
}
export interface ResolvedRow {
  phone: string; user_id: number | null; user_name: string | null; organization: string | null; matched: boolean;
  also_in: { campaign_id: number; name: string; ends_at: string }[];
}
export interface ResolveResponse { matched: number; unmatched: number; overlapping: number; rows: ResolvedRow[]; }

export interface Collision {
  campaign_id: number; name: string; status: CampaignStatus | null;
  overlap_start: string; overlap_end: string; overlap_days: number; topups_est: number;
  winner: string; winner_campaign_id: number | null; new_wins_share: number | null;
  delta_rupees: number; decided_by: "priority" | "better_rate";
}

export interface AudienceCount { users: number; reachable: number; }

/** Filters for GET /campaigns. All optional and combinable. `from`/`to` match
 *  campaigns whose window OVERLAPS the range, not ones contained by it — a
 *  campaign running 25 Aug to 5 Sep is a September campaign to anyone asking. */
export interface CampaignListParams {
  kind?: CampaignKind;
  /** one status, or a comma list: "live,scheduled" */
  status?: string;
  /** substring of name, slug or headline */
  q?: string;
  /** repeatable and ANDed — axios serialises string[] as repeated params */
  tag?: string[];
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/** A tag is an entity, not a label: it carries a utm_campaign referenced from
 *  outside this table, and the vocabulary is read from its own small table
 *  rather than scanned out of every campaign on every page load. */
export interface CampaignTag {
  id: number;
  slug: string;
  label: string;
  description: string | null;
  colour: string | null;
  utm_campaign: string | null;
  archived_at: string | null;
  /** how many campaigns carry it — the number beside the filter chip */
  usage_count: number;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  counts: {
    kind: Partial<Record<CampaignKind, number>>;
    status: Partial<Record<CampaignStatus, number>>;
    total: number;
  };
  /** the tag vocabulary, for the filter chips. Filter by `slug`. */
  tags: CampaignTag[];
}

export interface TagUpdate {
  label?: string;
  description?: string | null;
  colour?: string | null;
  utm_campaign?: string | null;
  archived?: boolean;
}

export interface Insights {
  program: Record<string, any>;
  campaigns_enabled: boolean;
  bands: { band: string; lo: number; topups: number; users: number; gross: number }[];
  exact_amounts: { amount: number; n: number }[];
  monthly: { month: string; topups: number; users: number; gross: number; cashback: number; cashbacks: number }[];
  totals: {
    paid_all_time: number; paid_count: number; unspent_promo: number; wallets_with_promo: number;
    paid_this_month: number; paid_this_month_count: number;
  };
  charging: { avg_rate_kwh: number; avg_session_kwh: number; sessions: number };
  repeat: { earners: number; repeated_30d: number };
  audiences: Record<string, AudienceCount>;
  floor: number;
}

// 409 bodies from POST /campaigns/{id}/schedule
export interface LeadTimeShort {
  detail: "lead_time_short"; lead_hours_actual: number; lead_hours_required: number; shortfall_hours: number;
  ok: boolean; already_started: boolean; starts_at: string; now: string; message: string; waiver_endpoint: string;
}
export interface OverlapConflict { detail: "overlap"; collisions: Collision[]; message: string; }
export interface AlreadyStarted { detail: "already_started"; message: string; }
export type ScheduleConflict = LeadTimeShort | OverlapConflict | AlreadyStarted | { detail: string; message?: string };

/** What a customer will actually receive. Built server-side by the same
 *  _copy() the dispatcher sends with, so the preview cannot drift from the
 *  message -- a preview that is merely similar to the real thing is worse than
 *  none, because it gets trusted. */
export interface PreviewMessage { kind: "teaser" | "announce" | "reminder"; title: string; body: string; }
export interface PreviewResponse {
  tz: string;
  image: string | null;
  channels: { push: boolean; inapp: boolean };
  banner_title: string;
  messages: PreviewMessage[];
}
export interface PreviewRequest {
  name: string;
  kind: CampaignKind;
  headline: string | null;
  subline: string | null;
  starts_at: string | null;
  ends_at: string | null;
  image_url: string | null;
  announce_push: boolean;
  announce_inapp: boolean;
}

/** Who an extra image is for. null is everyone. */
export type MediaAudience = "earned_here" | "near_miss" | "dormant" | "holding_promo" | "benefited";
/** An A/B arm. Images in one slot (same audience, same window) with a
 *  variant split customers evenly, at random but stably. */
export type MediaVariant = "A" | "B" | "C" | "D";

/** One of a campaign's extra images. The campaign's own image_url stays the
 *  default; the server picks an extra image instead when its rules match.
 *  Timestamps are naive UTC like every other one here; show_until is exclusive. */
export interface CampaignMedia {
  id: number;
  campaign_id: number;
  image_url: string;
  label: string | null;
  show_from: string | null;
  show_until: string | null;
  audience: MediaAudience | null;
  variant: MediaVariant | null;
  created_by: string;
  created_at: string;
}

/** A stretch of the campaign and the image shown in it. media_id null is the main image. */
export interface MediaSpan { from: string; until: string; media_id: number | null; }
/** audience null is everyone; there is one more per audience in use. */
export interface MediaTimeline { audience: MediaAudience | null; spans: MediaSpan[]; }
export interface MediaList { items: CampaignMedia[]; timeline: MediaTimeline[]; }

/** The multipart fields beside the file. A field left out is null. */
export interface MediaCreate {
  label?: string | null;
  show_from?: string | null;
  show_until?: string | null;
  audience?: MediaAudience | null;
  variant?: MediaVariant | null;
}

export type MediaField = "label" | "show_from" | "show_until" | "audience" | "variant";
/** A field left out keeps its value; to set one to null, name it in `clear`. */
export interface MediaUpdate {
  label?: string;
  show_from?: string;
  show_until?: string;
  audience?: MediaAudience;
  variant?: MediaVariant;
  clear?: MediaField[];
}

export interface MediaStat {
  /** null is the main image */
  media_id: number | null;
  /** carried on the row so a removed image can still be named and shown */
  label: string | null;
  image_url: string | null;
  /** null unless the image was removed */
  removed_at: string | null;
  shown_to: number;
  via_app: number;
  via_push: number;
  topped_up: number;
  topup_amount: number;
  cashbacks: number;
  cashback_amount: number;
  /** topped_up / shown_to as a fraction (0.12 is 12%); null when there is nothing to divide */
  conversion: number | null;
}
export interface MediaTest {
  audience: MediaAudience | null;
  show_from: string | null;
  show_until: string | null;
  /** media ids, one per arm */
  arms: number[];
  small_sample: boolean;
}
export interface MediaStats { items: MediaStat[]; tests: MediaTest[]; }

/** One push before a campaign starts. The campaign's own teaser_at is one of
 *  them, the primary (id null), edited through PUT as before; the others are
 *  added and removed here. Timestamps are naive UTC like every other one here. */
export interface Teaser {
  /** null is the campaign's own teaser_at */
  id: number | null;
  primary: boolean;
  send_at: string;
  sent_at: string | null;
  /** the push title as it will read when sent */
  title: string;
  body: string;
  created_by: string | null;
  created_at: string | null;
}
/** In send order, the primary included; `limit` counts it too. */
export interface TeaserList { limit: number; items: Teaser[]; }

export const campaignsApi = {
  // Repeated `tag` params rather than a comma list, because a comma is legal
  // inside nothing here but the serialiser has to be told which it is.
  list: (params?: CampaignListParams) =>
    axiosInstance.get<CampaignListResponse>(`${BASE}/campaigns`, {
      params,
      paramsSerializer: { indexes: null },
    }),
  create: (data: CampaignCreate) => axiosInstance.post<Campaign>(`${BASE}/campaigns`, data),
  get: (id: number) => axiosInstance.get<CampaignDetail>(`${BASE}/campaigns/${id}`),
  update: (id: number, data: CampaignUpdate) => axiosInstance.put<Campaign>(`${BASE}/campaigns/${id}`, data),
  cancel: (id: number) => axiosInstance.post<Campaign>(`${BASE}/campaigns/${id}/cancel`),
  end: (id: number, note?: string) => axiosInstance.post<Campaign>(`${BASE}/campaigns/${id}/end`, { note: note ?? null }),

  /** Artwork for the push. Multipart, because the file goes to the public
   *  bucket beside the station and device logos -- the campaign row only ever
   *  holds the resulting URL. Both return the updated campaign. */
  uploadImage: (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return axiosInstance.post<Campaign>(`${BASE}/campaigns/${id}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteImage: (id: number) => axiosInstance.delete<Campaign>(`${BASE}/campaigns/${id}/image`),

  /** Extra images, each with its own window, audience and A/B arm. Editable
   *  while draft, scheduled or live; 409 once ended or cancelled. The list
   *  comes with the server's timeline of which image shows when. POST and
   *  PATCH refuse two images with the same audience and window unless both
   *  are A/B arms: 400 slot_taken or bad_variant, with a message. */
  media: (id: number) => axiosInstance.get<MediaList>(`${BASE}/campaigns/${id}/media`),
  addMedia: (id: number, file: File, fields: MediaCreate = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    // Left out rather than sent empty: an omitted field is null.
    (Object.keys(fields) as (keyof MediaCreate)[]).forEach((k) => {
      const v = fields[k];
      if (v != null && v !== "") fd.append(k, v);
    });
    return axiosInstance.post<CampaignMedia>(`${BASE}/campaigns/${id}/media`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateMedia: (id: number, mediaId: number, data: MediaUpdate) =>
    axiosInstance.patch<CampaignMedia>(`${BASE}/campaigns/${id}/media/${mediaId}`, data),
  deleteMedia: (id: number, mediaId: number) =>
    axiosInstance.delete<{ removed: number }>(`${BASE}/campaigns/${id}/media/${mediaId}`),
  mediaStats: (id: number) => axiosInstance.get<MediaStats>(`${BASE}/campaigns/${id}/media/stats`),

  /** Teasers: pushes before the start. Added and removed while draft or
   *  scheduled; 409 not_editable after. POST refuses a time with a 400 and a
   *  message: too_many_teasers, teaser_after_start, teaser_in_past,
   *  teaser_quiet_hours, teaser_too_close. DELETE of a sent one is 409 already_sent. */
  teasers: (id: number) => axiosInstance.get<TeaserList>(`${BASE}/campaigns/${id}/teasers`),
  addTeaser: (id: number, send_at: string) =>
    axiosInstance.post<Teaser>(`${BASE}/campaigns/${id}/teasers`, { send_at }),
  deleteTeaser: (id: number, teaserId: number) =>
    axiosInstance.delete<{ removed: number }>(`${BASE}/campaigns/${id}/teasers/${teaserId}`),

  /** Allowed for draft, scheduled AND live — PUT /campaigns/{id} stays
   *  draft-only, and the caps are the one thing an operator has to be able to
   *  pull back without ending a campaign. A REPLACE, not a patch: a key left
   *  out takes the documented default rather than keeping what was stored, so
   *  always send the whole config. The server rejects an unknown key. */
  setBoost: (id: number, boost: CampaignBoost) =>
    axiosInstance.put<Campaign>(`${BASE}/campaigns/${id}/boost`, boost),

  /** Works on an unsaved draft: the operator is still typing. */
  preview: (data: PreviewRequest) => axiosInstance.post<PreviewResponse>(`${BASE}/campaigns/preview`, data),

  upsertTier: (id: number, t: CampaignTier) => axiosInstance.post<{ id: number }>(`${BASE}/campaigns/${id}/tiers`, t),
  deleteTier: (id: number, tierId: number) => axiosInstance.delete(`${BASE}/campaigns/${id}/tiers/${tierId}`),

  members: (id: number, limit = 500) =>
    axiosInstance.get<{ count: number; members: Member[] }>(`${BASE}/campaigns/${id}/members`, { params: { limit } }),
  resolveMembers: (id: number, phones: string[], user_ids: number[] = []) =>
    axiosInstance.post<ResolveResponse>(`${BASE}/campaigns/${id}/members/resolve`, { phones, user_ids }),
  addMembers: (id: number, phones: string[], user_ids: number[] = [], note?: string) =>
    axiosInstance.post<{ added: number; unmatched: string[]; count: number }>(`${BASE}/campaigns/${id}/members`, { phones, user_ids, note: note ?? null }),
  deleteMember: (id: number, userId: number) => axiosInstance.delete(`${BASE}/campaigns/${id}/members/${userId}`),
  audience: (id: number) =>
    axiosInstance.get<{ audience: Audience; counts: Record<string, AudienceCount> }>(`${BASE}/campaigns/${id}/audience`),

  forecast: (id: number, history_days = 90) =>
    axiosInstance.post<ForecastResponse>(`${BASE}/campaigns/${id}/forecast`, { history_days }, { timeout: 60000 }),
  collisions: (id: number, history_days = 90) =>
    axiosInstance.get<{ collisions: Collision[] }>(`${BASE}/campaigns/${id}/collisions`, { params: { history_days }, timeout: 60000 }),
  schedule: (id: number, accept_overlap = false) =>
    axiosInstance.post<Campaign>(`${BASE}/campaigns/${id}/schedule`, { accept_overlap }, { timeout: 60000 }),

  raiseLaunchRequest: (id: number, reason: string) =>
    axiosInstance.post<LaunchRequest>(`${BASE}/campaigns/${id}/launch-request`, { reason }),
  launchRequests: (params?: { status?: LaunchRequest["status"]; limit?: number }) =>
    axiosInstance.get<LaunchRequest[]>(`${BASE}/launch-requests`, { params }),
  approveLaunchRequest: (rid: number, note?: string) =>
    axiosInstance.post<LaunchRequest>(`${BASE}/launch-requests/${rid}/approve`, { note: note ?? null }),
  rejectLaunchRequest: (rid: number, note: string) =>
    axiosInstance.post<LaunchRequest>(`${BASE}/launch-requests/${rid}/reject`, { note }),

  // The vocabulary on its own, for the builder's suggestions — a read of a
  // small table rather than a campaign list fetched only for its tags.
  tags: (include_archived = false) =>
    axiosInstance.get<CampaignTag[]>(`${BASE}/tags`, { params: { include_archived } }),
  updateTag: (slug: string, data: TagUpdate) => axiosInstance.put<CampaignTag>(`${BASE}/tags/${slug}`, data),

  performance: (id: number) => axiosInstance.get<Performance>(`${BASE}/campaigns/${id}/performance`),
  audit: (id: number, limit = 200) => axiosInstance.get<AuditRow[]>(`${BASE}/campaigns/${id}/audit`, { params: { limit } }),
  rebuildCounters: (id: number) => axiosInstance.post<{ awarded_count: number; awarded_amount: number }>(`${BASE}/campaigns/${id}/rebuild-counters`),
  insights: () => axiosInstance.get<Insights>(`${BASE}/insights`, { timeout: 60000 }),
};

/** The 409 body of a schedule attempt, or null when the error was something else. */
export const scheduleConflict = (e: any): ScheduleConflict | null => {
  const d = e?.response?.status === 409 ? e?.response?.data?.detail : null;
  return d && typeof d === "object" && typeof d.detail === "string" ? (d as ScheduleConflict) : null;
};
